/**
 * iter87.199.18 — Tile Help Popover.
 *
 * Small `?` icon anchored top-right of every admin bento tile. Tap
 * opens a BentoModal explaining what the tile is for, what lives
 * inside, and one best-practice tip. `e.stopPropagation` on the
 * button so the tile itself doesn't open when the user taps the `?`.
 *
 * Copy is centralised in `TILE_HELP` below — future edits are a
 * single-file change. Custom-template tiles (teams / schedule /
 * bracket_status) carry an explicit `[Custom template only]` note
 * so admins on API-driven templates never see them by accident.
 */
import { useState } from "react";
import { HelpCircle, X } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TrophyTokensWatchButton } from "@/components/TrophyTokensVideoModal";

// -----------------------------------------------------------------
// Copy map. Order/keys mirror `visibleTabs` in `AdminPanel.jsx`.
// -----------------------------------------------------------------

export const TILE_HELP = {
    // ----- Tournament scope (all templates) -----
    scoring: {
        title: "Scoring",
        summary: "Manage the point system for this tournament. Turn on / off pre-season and in-season draft features here.",
        bullets: [
            "W/L/T & KO stage points",
            "Champions Pick, Power Picks & in-season boosts",
        ],
        tip: "Defaults provide the best gameplay. Set BEFORE the first game — scoring locks once the season kicks off.",
    },
    draft: {
        title: "Draft",
        summary: "Kick off the auto-draft so every crewmate gets their teams before the season starts.",
        bullets: [
            "Auto-draft runs when you hit Start",
            "Snake order across rounds",
        ],
        tip: "Run the draft before the first game — it locks the moment play begins.",
    },
    live_sync: {
        title: "Live Sync",
        summary: "See what's powering scores for this tournament and confirm the feed is healthy.",
        bullets: [
            "Live feed status + last sync time",
            "Field map (API team → your team)",
            "Manual refresh + re-sync controls where supported",
        ],
        tip: "Green \"Feed live\" = you're good. If it goes amber, we're already investigating.",
    },
    trophy_tokens: {
        title: "Trophy Tokens",
        summary: "A season-long token pot for this tournament. Crewmates opt in, the admin sets how it pays out, and tokens are awarded to the real leaderboard winner(s) once the season ends. Pro+ only.",
        bullets: [
            "Set the pot: per-player or a fixed total, plus an optional house cut %",
            "Pick Winner Take All (top score takes it all) or Podium (split 1st/2nd/3rd)",
            "Two-step roster: Opt In → Confirm each crewmate before kickoff",
            "Lock Pool freezes everything early (permanent); Finalize Awards pays out once the season actually ends",
        ],
        tip: "Winner is auto-derived from the leaderboard at Finalize — you never pick a winner manually.",
        // iter87.536 (user-requested) — surface the same walkthrough
        // video here too, since this "?" popout is the first thing an
        // admin sees before opening the tile.
        watchVideo: true,
    },
    settings: {
        title: "Tournament Settings",
        summary: "Per-tournament controls — these only affect this tournament, not the whole rivalry.",
        bullets: [
            "Set this tournament's timezone (for schedule display)",
            "Opt yourself in or out as a player for this tournament",
            "Restore original seeding + email a season recap",
            "Download a Picks Audit PDF (spread/boost tournaments only)",
        ],
        tip: "Looking for invites, spectator mode, or co-admins? Those live under Rivalry Admin, not here.",
    },
    danger_zone: {
        title: "Danger Zone",
        summary: "One-way, destructive actions. Live here so nothing gets fat-fingered from a routine settings visit.",
        bullets: [
            "Lock the tournament (read-only mode)",
            "Clear results, reset picks",
            "Mark complete (final trophy pass)",
        ],
        tip: "Every action asks you to type a confirm word. Read it twice.",
    },

    // ----- Tournament scope (custom template only) -----
    teams: {
        title: "Teams",
        customOnly: true,
        summary: "Manage the team roster for this custom tournament.",
        bullets: [
            "Add, rename, or remove teams",
            "Set seed, group, and bracket slot",
            "Bulk-add from a paste list",
        ],
        tip: "Lock the roster before draft — team edits after the draft can shift picks.",
    },
    schedule: {
        title: "Schedule",
        customOnly: true,
        summary: "Manage the fixture list and enter results by hand.",
        bullets: [
            "Add, edit, or delete matches",
            "Enter scores / mark played",
            "Reset a fixture back to unplayed",
        ],
        tip: "Custom tournaments have no live feed — every score is your call to enter.",
    },
    bracket_status: {
        title: "Bracket Status",
        customOnly: true,
        summary: "Move winners through the bracket and stamp rounds as complete.",
        bullets: [
            "Advance teams to the next round",
            "Mark a round as played (unlocks the next)",
        ],
        tip: "Advance winners round-by-round; the platform doesn't auto-advance on custom.",
    },

    // ----- League scope -----
    players: {
        title: "Crewmates",
        summary: "See who's in the rivalry and manage the roster.",
        bullets: [
            "Add / remove crewmates",
            "Rename a crewmate",
            "Change a crewmate's email",
            "Find a crewmate's personal code",
        ],
        tip: "Removing a crewmate mid-season keeps their historical picks — they just stop appearing on the leaderboard.",
    },
    invites: {
        title: "Invites",
        summary: "Get new crewmates into the rivalry via email.",
        bullets: [
            "Send email invites (capped per rivalry — fewer slots if you're also playing)",
            "See pending vs. joined invites",
            "Resend or revoke an invite anytime",
        ],
        tip: "Strict invites is always on — only people you invite here (or add via Crewmates) can join.",
    },
    messages: {
        title: "Messages",
        summary: "Broadcast one-way announcements to the whole crew, plus the tools that help new crewmates join.",
        bullets: [
            "Compose + send an announcement — posts to everyone's dashboard Smack Stream",
            "Copy ready-made invite text or your rivalry's share link",
            "Toggle whether crewmates can post to the Smack Stream too",
        ],
        tip: "Keep announcements punchy — long messages get scrolled past.",
    },
    co_admins: {
        title: "Co-Admins",
        summary: "See who holds co-admin powers in this rivalry, and fine-tune exactly what they're allowed to touch.",
        bullets: [
            "View the rivalry owner + every active co-admin",
            "Narrow a co-admin's permissions (Scoring, Draft, Trophy Tokens, Invites, and more)",
            "Promoting or demoting a co-admin is Site-Admin-only",
        ],
        tip: "Only the Site Admin can add/remove a co-admin — email admin@tinycrewrivalries.com to request one.",
    },
    // League scope reuses the `settings` key with different rendering
    // upstream. We surface distinct copy via the `settingsScope` prop
    // on the popover ('league' or 'tournament').
    settings_league: {
        title: "Rivalry Settings",
        summary: "Rivalry-wide controls that apply across every tournament you run here.",
        bullets: [
            "Spectator mode — let non-players view the leaderboard via a public link",
            "Report a bug or feature idea to the Tiny Crew Rivalries team",
            "Danger Zone: archive / close the whole rivalry",
        ],
        tip: "Invites, Messages, and Co-Admins each have their own tile now — this one's identity + spectator + danger zone.",
    },
};


export default function TileHelpPopover({ tileKey, scope = "tournament" }) {
    const [open, setOpen] = useState(false);

    // League-scope `settings` gets its own copy block.
    const effectiveKey = (tileKey === "settings" && scope === "league")
        ? "settings_league"
        : tileKey;
    const help = TILE_HELP[effectiveKey];
    if (!help) return null;

    const testKey = tileKey === "settings" && scope === "league" ? "settings-league" : tileKey;

    return (
        <>
            <button
                type="button"
                data-testid={`tile-help-${testKey}-btn`}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                }}
                className="absolute top-2 right-2 p-1 text-zinc-500 hover:text-cyber transition-colors"
                aria-label={`Help — ${help.title}`}
            >
                <HelpCircle className="w-4 h-4" />
            </button>

            <BentoModal
                open={open}
                onClose={() => setOpen(false)}
                testId={`tile-help-${testKey}-popover`}
                backdropClassName="items-end sm:items-center justify-center sm:p-4"
                panelClassName="brutal-card w-full sm:max-w-md max-h-[85dvh] overflow-y-auto bg-ink border-2 border-cyber shadow-2xl"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 bg-ink border-b-2 border-zinc-800 px-4 py-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <HelpCircle className="w-4 h-4 text-cyber shrink-0" />
                        <h2 className="font-display font-extrabold text-base uppercase tracking-wide truncate">
                            {help.title}
                        </h2>
                    </div>
                    <button
                        onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                        data-testid={`tile-help-${testKey}-close-btn`}
                        className="p-1.5 text-zinc-500 hover:text-white shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 space-y-3">
                    {help.customOnly && (
                        <p
                            data-testid={`tile-help-${testKey}-custom-only`}
                            className="text-[10px] uppercase tracking-widest font-bold text-volt border border-volt/40 bg-volt/5 px-2 py-1 inline-block"
                        >
                            Custom template only
                        </p>
                    )}

                    {help.watchVideo && (
                        <TrophyTokensWatchButton testId={`tile-help-${testKey}-watch-btn`} />
                    )}

                    <p className="text-sm text-zinc-300 leading-relaxed">{help.summary}</p>

                    {help.bullets?.length > 0 && (
                        <ul className="space-y-1.5 pl-4">
                            {help.bullets.map((b, i) => (
                                <li
                                    key={i}
                                    className="text-[13px] text-zinc-400 list-disc leading-snug"
                                    data-testid={`tile-help-${testKey}-bullet-${i}`}
                                >
                                    {b}
                                </li>
                            ))}
                        </ul>
                    )}

                    {help.tip && (
                        <div className="brutal-card-light p-3 border-cyber/30 bg-cyber/5">
                            <p className="text-[10px] uppercase tracking-widest font-bold text-cyber mb-1">
                                Tip
                            </p>
                            <p className="text-[12px] text-zinc-300 leading-snug">{help.tip}</p>
                        </div>
                    )}
                </div>
            </BentoModal>
        </>
    );
}
